import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PDFViewer, Page, Text, View, Document, StyleSheet, Font, Svg } from '@react-pdf/renderer';
import axios from 'axios';
import './styles.css';
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Label from '../components/label';
import Scrollbar from '../components/scrollbar';
import HistoryHuntersDevices from '../components/HistoryHuntersDevices';

import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

const BACKEND_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

// ----------------------------------------------------------------------
// Kolone tabele na ekranu
// search: kako radi pretraga kad je ta kolona izabrana (klikom na naslov)
//   'exact'   - tačna brojčana vrijednost (ID)
//   'fullname'- ime + prezime sadrži tekst
//   'text'    - sadrži tekst
//   'numeric' - broj ili izraz: 3, >0, >=5, <2, <=1
//   null      - bez pretrage (samo sortiranje)
// sortable: false - kolona se ne može sortirati

const TABLE_HEAD = [
  { id: 'id', label: 'Broj', alignRight: false, search: 'exact' },
  { id: 'ime', label: 'Ime', alignRight: false, search: 'fullname' },
  { id: 'prezime', label: 'Prezime', alignRight: false, search: 'text' },
  { id: 'mjesto', label: 'Mjesto', alignRight: false, search: 'text' },
  { id: 'drzava', label: 'Država', alignRight: false, search: 'text' },
  { id: 'username', label: 'Username', alignRight: false, search: 'text' },
  { id: 'password', label: 'Password', alignRight: false, search: null, sortable: false },
  { id: 'datumregistracije', label: 'Registrovan', alignRight: false, search: null },
  { id: 'ukupno', label: 'Uređaja', alignRight: false, search: 'numeric' },
  { id: 'online', label: 'Online', alignRight: false, search: 'numeric' },
  { id: 'offline', label: 'Offline', alignRight: false, search: 'numeric' },
  { id: 'onlinetoday', label: 'Online danas', alignRight: false, search: 'numeric' },
  { id: 'aktivni', label: 'Aktivni', alignRight: false, search: 'numeric' },
  { id: 'saldoNegativan', label: 'Saldo < 0', alignRight: false, search: 'numeric' },
  { id: 'istekli', label: 'Istekli', alignRight: false, search: 'numeric' },
  { id: 'update', label: 'Update', alignRight: false, search: null, sortable: false },
  { id: 'show', label: 'Show/Hide', alignRight: false, search: null, sortable: false },
  { id: 'delete', label: 'Delete', alignRight: false, search: null, sortable: false },
];

const getColumn = (id) => TABLE_HEAD.find((c) => c.id === id);

// ----------------------------------------------------------------------
// Kolone PDF izvještaja (zbir širina = 100%)

const REPORT_COLS = [
  { key: 'rb', label: 'R.br.', width: '4%' },
  { key: 'imePrezime', label: 'Ime i prezime', width: '20%', left: true },
  { key: 'mjesto', label: 'Mjesto', width: '13%', left: true },
  { key: 'drzava', label: 'Država', width: '14%', left: true },
  { key: 'ukupno', label: 'Uređaja', width: '7%' },
  { key: 'online', label: 'Online', width: '7%' },
  { key: 'offline', label: 'Offline', width: '7%' },
  { key: 'onlinetoday', label: 'Online danas', width: '7%' },
  { key: 'aktivni', label: 'Aktivni', width: '7%' },
  { key: 'saldoNegativan', label: 'Saldo < 0', width: '7%' },
  { key: 'istekli', label: 'Istekli', width: '7%' },
];

const PRAZNA_STATISTIKA = {
  ukupno: 0,
  online: 0,
  offline: 0,
  onlinetoday: 0,
  aktivni: 0,
  saldoNegativan: 0,
  istekli: 0,
};

// ----------------------------------------------------------------------
// Sortiranje i pretraga

function compareValues(a, b) {
  // Brojevi numerički, tekst abecedno (uz ispravno Č, Ć, Š, Ž, Đ)
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) {
    return na - nb;
  }
  return String(a ?? '').localeCompare(String(b ?? ''), 'bs', { sensitivity: 'base' });
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => compareValues(b[orderBy], a[orderBy])
    : (a, b) => compareValues(a[orderBy], b[orderBy]);
}

// Brojčana pretraga: '3', '=3', '>0', '>=5', '<2', '<=1'
function matchNumeric(value, query) {
  const q = query.replace(/\s+/g, '');
  if (/^(>=|<=|>|<|=)?$/.test(q)) return true; // tek započet izraz, npr. samo '>'

  const m = q.match(/^(>=|<=|>|<|=)?(-?\d+)$/);
  if (!m) return false;

  const op = m[1] || '=';
  const n = Number(m[2]);
  const v = Number(value);

  switch (op) {
    case '>': return v > n;
    case '<': return v < n;
    case '>=': return v >= n;
    case '<=': return v <= n;
    default: return v === n;
  }
}

function matchesSearch(row, field, query) {
  const column = getColumn(field);
  const mode = column ? column.search : null;
  const q = query.trim().toLowerCase();

  switch (mode) {
    case 'exact':
      return /^\d+$/.test(q) ? Number(row[field]) === Number(q) : false;
    case 'fullname':
      return `${row.ime} ${row.prezime}`.toLowerCase().includes(q);
    case 'text':
      return String(row[field] ?? '').toLowerCase().includes(q);
    case 'numeric':
      return matchNumeric(row[field], q);
    default:
      return true; // kolona bez pretrage
  }
}

function applySortFilter(array, comparator, query, field) {
  const sorted = array
    .map((el, index) => [el, index])
    .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
    .map((el) => el[0]);

  if (!query || !query.trim()) return sorted;

  return sorted.filter((row) => matchesSearch(row, field, query));
}

function getSearchPlaceholder(field) {
  const column = getColumn(field);
  if (!column) return 'Traži...';

  switch (column.search) {
    case 'exact':
      return `Traži po: ${column.label} (tačan broj)`;
    case 'fullname':
      return 'Traži po: Ime i prezime';
    case 'text':
      return `Traži po: ${column.label}`;
    case 'numeric':
      return `Traži po: ${column.label} (npr. 3, >0, <=2)`;
    default:
      return `Pretraga nije dostupna za: ${column.label}`;
  }
}

// ----------------------------------------------------------------------
// Statistika uređaja po pretplatniku (računa se na frontendu)

function izracunajStatistiku(devices) {
  const danas = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
  const map = {};

  devices.forEach((d) => {
    const sid = d.subscriberId;
    if (!map[sid]) map[sid] = { ...PRAZNA_STATISTIKA };
    const s = map[sid];

    s.ukupno += 1;
    if (Number(d.online) > 0) s.online += 1;
    else s.offline += 1;
    if (Number(d.onlinetoday) > 0) s.onlinetoday += 1;
    if (Number(d.aktivan) > 0) s.aktivni += 1;
    if (Number(d.saldo) < 0) s.saldoNegativan += 1; // promijeni u <= 0 ako i 0 znači "bez kredita"
    if (d.datumend && d.datumend < danas) s.istekli += 1;
  });

  return map;
}

// ----------------------------------------------------------------------
// Ćelija sa brojem u tabeli

function StatCell({ value, color = 'default' }) {
  return (
    <TableCell align="center">
      {value > 0 ? (
        <Label color={color}>{value}</Label>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          0
        </Typography>
      )}
    </TableCell>
  );
}

// ----------------------------------------------------------------------
// PDF font i stilovi (definisani jednom, izvan komponente)

Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

const styles = StyleSheet.create({
  body: {
    paddingTop: 40,
    paddingBottom: 65,
    paddingHorizontal: 30,
    fontFamily: 'Roboto',
    fontSize: 10,
  },
  textmedium: {
    margin: 3,
    fontSize: 12,
    fontFamily: 'Roboto',
    paddingHorizontal: 20,
  },
  textmediumlarge: {
    fontSize: 20,
    fontFamily: 'Roboto',
  },
  svgTitle: {
    marginVertical: 5,
    paddingHorizontal: 20,
  },
  summary: {
    fontSize: 10,
    marginBottom: 12,
    paddingHorizontal: 20,
    lineHeight: 1.5,
  },
  table: {
    display: 'table',
    width: 'auto',
  },
  tableRow: {
    margin: 0,
    flexDirection: 'row',
  },
  tableHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    backgroundColor: '#e8ecf4',
  },
  rowOdd: {
    backgroundColor: '#f5f5f5',
  },
  tableCellLeft: {
    marginTop: 4,
    marginBottom: 4,
    marginLeft: 3,
    fontSize: 9,
    textAlign: 'left',
  },
  tableCellCenter: {
    marginTop: 4,
    marginBottom: 4,
    fontSize: 9,
    textAlign: 'center',
  },
  officer: {
    paddingTop: 50,
    left: 250,
  },
  officertext: {
    margin: 0,
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'Roboto',
  },
  mp: {
    position: 'absolute',
    fontSize: 12,
    bottom: 60,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
});

// ----------------------------------------------------------------------
// PDF dokument

function SubscribersReportAll({ rows, datum }) {
  const totals = rows.reduce(
    (acc, s) => {
      Object.keys(acc).forEach((k) => {
        acc[k] += s[k] || 0;
      });
      return acc;
    },
    { ...PRAZNA_STATISTIKA }
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.body} wrap>
        <Text style={styles.textmedium}>{datum}</Text>

        <Svg style={styles.svgTitle} width="800" height="40" viewBox="0 0 800 40">
          <Text style={styles.textmediumlarge} x="170" y="30">
            Pregled evidencije pretplatnika i uređaja
          </Text>
        </Svg>

        <View style={styles.summary}>
          <Text>Korisnika u izvještaju: {rows.length}</Text>
          <Text>
            Ukupno uređaja: {totals.ukupno}   |   Online: {totals.online}   |   Offline: {totals.offline}   |   Online danas: {totals.onlinetoday}
          </Text>
          <Text>
            Aktivnih: {totals.aktivni}   |   Saldo ispod nule: {totals.saldoNegativan}   |   Istekla pretplata: {totals.istekli}
          </Text>
        </View>

        <View style={styles.table}>
          <View style={{ ...styles.tableRow, ...styles.tableHeader }}>
            {REPORT_COLS.map((c) => (
              <View key={c.key} style={{ width: c.width }}>
                <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>{c.label}</Text>
              </View>
            ))}
          </View>

          {rows.map((s, index) => {
            const values = {
              rb: `${index + 1}.`,
              imePrezime: `${s.ime} ${s.prezime}`,
              mjesto: s.mjesto,
              drzava: s.drzava,
              ukupno: s.ukupno,
              online: s.online,
              offline: s.offline,
              onlinetoday: s.onlinetoday,
              aktivni: s.aktivni,
              saldoNegativan: s.saldoNegativan,
              istekli: s.istekli,
            };

            return (
              <View
                key={s.id}
                wrap={false}
                style={index % 2 ? { ...styles.tableRow, ...styles.rowOdd } : styles.tableRow}
              >
                {REPORT_COLS.map((c) => (
                  <View key={c.key} style={{ width: c.width }}>
                    <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>
                      {String(values[c.key] ?? '')}
                    </Text>
                  </View>
                ))}
              </View>
            );
          })}
        </View>

        <View style={styles.officer}>
          <Text style={styles.officertext}>______________________________</Text>
          <Text style={styles.officertext}>(Knjigovođa)</Text>
        </View>

        <Text style={styles.mp}>M.P.</Text>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
}

// ----------------------------------------------------------------------

export default function HistoryHuntersDevicesAll() {
  const { user: currentUser } = useSelector((state) => state.auth);

  const [subscriberid, setSubscriberId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  // Kolona po kojoj se pretražuje; na startu ime i prezime, iako je sortirano po ID-u
  const [searchField, setSearchField] = useState('ime');
  const searchInputRef = useRef(null);
  const [subscribers, setSubscribers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [samoSaUredjajima, setSamoSaUredjajima] = useState(true);

  // Dijalog za brisanje: mode = 'checking' | 'blocked' | 'confirm' | 'error'
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    mode: 'checking',
    subscriber: null,
    brojUredjaja: 0,
    poruka: '',
  });
  const [deleting, setDeleting] = useState(false);

  // --- Dohvatanje podataka ---

  const getAllSubscribers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/subscribers`);
      setSubscribers(response.data);
    } catch (err) {
      console.log('Greška pri učitavanju pretplatnika', err);
    }
  };

  const getAllDevices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/devices`);
      setDevices(response.data);
    } catch (err) {
      console.log('Greška pri učitavanju uređaja', err);
    }
  };

  // Učitaj jednom, pri otvaranju stranice
  useEffect(() => {
    if (!currentUser) return;
    getAllSubscribers();
    getAllDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Spajanje statistike u pretplatnike ---

  const deviceStats = useMemo(() => izracunajStatistiku(devices), [devices]);

  const subscribersWithStats = useMemo(
    () => subscribers.map((s) => ({ ...s, ...(deviceStats[s.id] || PRAZNA_STATISTIKA) })),
    [subscribers, deviceStats]
  );

  const filteredSubscribers = useMemo(
    () => applySortFilter(subscribersWithStats, getComparator(order, orderBy), filterName, searchField),
    [subscribersWithStats, order, orderBy, filterName, searchField]
  );

  const reportRows = useMemo(
    () => (samoSaUredjajima ? filteredSubscribers.filter((s) => s.ukupno > 0) : filteredSubscribers),
    [filteredSubscribers, samoSaUredjajima]
  );

  // Svi hookovi su iznad ove provjere
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // --- Handleri ---

  const setData = (data) => {
    const { id, ime, prezime, telefon, godiste, mjesto, drzava, username, datumregistracije } = data;
    localStorage.setItem('id', id);
    localStorage.setItem('username', username);
    localStorage.setItem('datumregistracije', datumregistracije);
    localStorage.setItem('ime', ime);
    localStorage.setItem('prezime', prezime);
    localStorage.setItem('telefon', telefon);
    localStorage.setItem('godiste', godiste);
    localStorage.setItem('mjesto', mjesto);
    localStorage.setItem('drzava', drzava);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');

    // Klik na kolonu po kojoj se još ne pretražuje: pretraga prelazi na nju, stari tekst se briše
    if (property !== searchField) {
      setFilterName('');
      setPage(0);
      setSearchField(property);
      // Vrati kursor u polje za pretragu, da se odmah može kucati
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
    setOrderBy(property);
  };

  const handleClickShow = (event, id) => {
    setSubscriberId(id);
  };

  // --- Brisanje pretplatnika ---

  const zatvoriDeleteDialog = () => {
    if (deleting || deleteDialog.mode === 'checking') return;
    setDeleteDialog((prev) => ({ ...prev, open: false }));
  };

  const handleClickDelete = async (row) => {
    setDeleteDialog({ open: true, mode: 'checking', subscriber: row, brojUredjaja: 0, poruka: '' });

    try {
      // Svježa provjera, ne oslanjamo se na kolonu "Uređaja" u tabeli
      const response = await axios.get(`${BACKEND_URL}/devices?subscriberId=${row.id}`);
      const lista = Array.isArray(response.data) ? response.data : [];
      // Dodatno filtriranje za slučaj da backend ignoriše parametar i vrati sve uređaje
      const brojUredjaja = lista.filter((d) => Number(d.subscriberId) === Number(row.id)).length;

      setDeleteDialog({
        open: true,
        mode: brojUredjaja > 0 ? 'blocked' : 'confirm',
        subscriber: row,
        brojUredjaja,
        poruka: '',
      });
    } catch (err) {
      console.log('Greška pri provjeri uređaja', err);
      setDeleteDialog({
        open: true,
        mode: 'error',
        subscriber: row,
        brojUredjaja: 0,
        poruka: 'Nije moguće provjeriti uređaje ovog korisnika, pa je brisanje otkazano. Pokušajte ponovo.',
      });
    }
  };

  const handleConfirmDelete = async () => {
    const { subscriber } = deleteDialog;
    if (!subscriber) return;

    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/subscribers/${subscriber.id}`);

      // Ukloni iz tabele bez ponovnog učitavanja svih korisnika
      setSubscribers((prev) => prev.filter((s) => s.id !== subscriber.id));

      // Ako je obrisani bio prikazan u donjoj tabeli, resetuj je
      if (subscriberid === subscriber.id) setSubscriberId(0);

      // Ako je obrisan zadnji red na zadnjoj stranici, vrati se stranicu nazad
      const preostalo = filteredSubscribers.length - 1;
      if (page > 0 && page * rowsPerPage >= preostalo) setPage(page - 1);

      setDeleteDialog((prev) => ({ ...prev, open: false }));
    } catch (err) {
      console.log('Greška pri brisanju korisnika', err);
      setDeleteDialog((prev) => ({
        ...prev,
        mode: 'error',
        poruka: 'Brisanje korisnika nije uspjelo. Provjerite vezu sa serverom i pokušajte ponovo.',
      }));
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredSubscribers.length) : 0;

  const isNotFound = !filteredSubscribers.length && !!filterName;

  const today = new Date();
  const datum = `Datum: ${today.getDate()}.${today.getMonth() + 1}.${today.getFullYear()}. god.`;

  const ukupnoKolona = TABLE_HEAD.length;

  // --- Render ---

  return (
    <>
      <Helmet>
        <title> GPS Dogs Tracking System </title>
      </Helmet>

      <Dialog open={deleteDialog.open} onClose={zatvoriDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Brisanje korisnika</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {deleteDialog.mode === 'checking' && 'Provjeravam uređaje korisnika...'}

            {deleteDialog.mode === 'blocked' && deleteDialog.subscriber && (
              <>
                Korisnik{' '}
                <strong>
                  {deleteDialog.subscriber.ime} {deleteDialog.subscriber.prezime}
                </strong>{' '}
                ima {deleteDialog.brojUredjaja} {deleteDialog.brojUredjaja === 1 ? 'uređaj' : 'uređaja'} i ne može se
                obrisati. Prvo obrišite ili prebacite (Transfer) njegove uređaje.
              </>
            )}

            {deleteDialog.mode === 'confirm' && deleteDialog.subscriber && (
              <>
                Da li ste sigurni da želite obrisati korisnika{' '}
                <strong>
                  {deleteDialog.subscriber.ime} {deleteDialog.subscriber.prezime}
                </strong>{' '}
                ({deleteDialog.subscriber.username})? Ova akcija je nepovratna.
              </>
            )}

            {deleteDialog.mode === 'error' && deleteDialog.poruka}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          {deleteDialog.mode === 'confirm' ? (
            <>
              <Button onClick={zatvoriDeleteDialog} disabled={deleting}>
                Ne
              </Button>
              <Button color="error" onClick={handleConfirmDelete} disabled={deleting} autoFocus>
                {deleting ? 'Brišem...' : 'Da'}
              </Button>
            </>
          ) : (
            <Button onClick={zatvoriDeleteDialog} disabled={deleteDialog.mode === 'checking'}>
              U redu
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Container maxWidth={false}>
        <Typography variant="subtitle2" noWrap>
          Pregled evidencije pretplatnika
        </Typography>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterName}
            onFilterName={handleFilterByName}
            placeholder={getSearchPlaceholder(searchField)}
            autoFocus
            inputRef={searchInputRef}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
                <TableBody>
                  {filteredSubscribers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const {
                      id,
                      ime,
                      prezime,
                      mjesto,
                      drzava,
                      username,
                      password,
                      datumregistracije,
                      ukupno,
                      online,
                      offline,
                      onlinetoday,
                      aktivni,
                      saldoNegativan,
                      istekli,
                    } = row;

                    return (
                      <TableRow hover key={id} tabIndex={-1}>

                        <TableCell>
                          <Typography variant="subtitle2" noWrap>
                            {id}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{ime}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{prezime}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{mjesto}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{drzava}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{username}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{password}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>{datumregistracije}</Typography>
                        </TableCell>

                        <StatCell value={ukupno} color="info" />
                        <StatCell value={online} color="success" />
                        <StatCell value={offline} color="default" />
                        <StatCell value={onlinetoday} color="success" />
                        <StatCell value={aktivni} color="primary" />
                        <StatCell value={saldoNegativan} color="error" />
                        <StatCell value={istekli} color="warning" />

                        <TableCell>
                          <Link to="/SLOBiHClanovi/update">
                            <Button onClick={() => setData(row)}>Update</Button>
                          </Link>
                        </TableCell>

                        <TableCell>
                          <Button onClick={(event) => handleClickShow(event, id)}>Show</Button>
                        </TableCell>

                        <TableCell>
                          <Button color="error" onClick={() => handleClickDelete(row)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={ukupnoKolona} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={ukupnoKolona} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>
                            Nema rezultata
                          </Typography>
                          <Typography variant="body2">
                            Ništa nije pronađeno za &nbsp;
                            <strong>&quot;{filterName}&quot;</strong> u koloni {getColumn(searchField)?.label}.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredSubscribers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        <br />

        <Stack direction="row" alignItems="center" spacing={2}>
          <Button onClick={() => setLoading(!loading)}>
            {loading ? 'Zatvori izvještaj' : 'Generate Report'}
          </Button>
          <FormControlLabel
            control={
              <Switch checked={samoSaUredjajima} onChange={(e) => setSamoSaUredjajima(e.target.checked)} />
            }
            label="Samo korisnici s uređajima"
          />
        </Stack>

        {loading && (
          <div className="child">
            <PDFViewer className="child">
              <SubscribersReportAll rows={reportRows} datum={datum} />
            </PDFViewer>
          </div>
        )}
      </Container>

      <br />

      <div>
        <HistoryHuntersDevices vlasnik={subscriberid} />
      </div>
    </>
  );
}